/**
 * /app/$tenantSlug/settings/hiring-approvals — Settings → People & HR →
 * Hiring approvals (HR-admin). Wave K1.2.
 *
 * The per-tenant approval-gate toggles:
 *  - Require approval to OPEN a requisition.
 *  - Require approval to SEND an offer (the CTC breakup is what the
 *    approver signs off on).
 *  - Who approves: company admin or HR manager.
 *
 * Both gates are OFF by default — turning neither on is a no-op (hiring
 * behaves exactly as before). When a gate is ON, the owner submits the
 * requisition/offer for approval and only the configured approver (never
 * the submitter) can decide; the open/send action is blocked until
 * approved. The decision surfaces in the unified Approvals inbox.
 */

import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { toApiError } from "@/lib/api";
import {
  useHiringApprovalSettings,
  useUpdateHiringApprovalSettings,
} from "@/hooks/use-hiring";
import type { ApproverRole } from "@/types/hiring";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/hiring-approvals",
)({
  component: SettingsHiringApprovalsRoute,
});

function GatesCard() {
  const settingsQ = useHiringApprovalSettings();
  const updateMut = useUpdateHiringApprovalSettings();
  const s = settingsQ.data?.settings;

  async function patch(
    body: Parameters<typeof updateMut.mutateAsync>[0],
  ) {
    try {
      await updateMut.mutateAsync(body);
      toast.success("Approval settings saved");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Approval Gates</CardTitle>
        <CardDescription>
          Both gates are off by default. Turn one on and the owner submits
          for approval; only the chosen approver (never the submitter) can
          decide, and the action is blocked until approved. Decisions show
          up in the Approvals inbox.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {s == null ? (
          <Skeleton className="h-28 w-full" />
        ) : (
          <>
            <div className="flex items-start gap-2">
              <Checkbox
                id="require_requisition_approval"
                checked={s.require_requisition_approval}
                disabled={updateMut.isPending}
                onCheckedChange={(v) =>
                  void patch({ require_requisition_approval: v === true })
                }
              />
              <div>
                <Label
                  htmlFor="require_requisition_approval"
                  className="cursor-pointer"
                >
                  Require Approval to Open a Requisition
                </Label>
                <p className="text-xs text-muted-foreground">
                  A requisition cannot move to Open until it is approved.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="require_offer_approval"
                checked={s.require_offer_approval}
                disabled={updateMut.isPending}
                onCheckedChange={(v) =>
                  void patch({ require_offer_approval: v === true })
                }
              />
              <div>
                <Label
                  htmlFor="require_offer_approval"
                  className="cursor-pointer"
                >
                  Require Approval to Send an Offer
                </Label>
                <p className="text-xs text-muted-foreground">
                  An offer cannot be sent until it is approved. Editing the
                  CTC of an approved offer re-requires approval.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Approver</Label>
              <div className="flex gap-2">
                {(
                  [
                    { value: "company_admin", label: "Company Admin" },
                    { value: "hr_manager", label: "HR Manager" },
                  ] as { value: ApproverRole; label: string }[]
                ).map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    size="sm"
                    variant={
                      s.approver_role === opt.value ? "default" : "outline"
                    }
                    disabled={updateMut.isPending}
                    onClick={() =>
                      void patch({ approver_role: opt.value })
                    }
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Who signs off on requisitions and offers. A submitter can
                never approve their own request.
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SettingsHiringApprovalsRoute() {
  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Hiring Approvals</h2>
            <p className="text-muted-foreground text-sm">
              Gate requisition opening and offer sending behind an approval
              chain.
            </p>
          </div>
          <GatesCard />
        </div>
      </SettingsShell>
    </>
  );
}
